import { useEffect, useState } from "react"
import { Input as AntInput } from "antd"
import { Modal } from "components/ui/modals/Modal/Modal"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { useProjectNumber } from "hooks/useProjectNumber.hook"
import type { TOnChangeInput } from "types/default.type"
import style from "./ProjectNumberGateModal.module.scss"

type Props = {
	isOpen: boolean
	onClose: () => void
	/** Called after the project number is captured — continue to the create-quotation flow. */
	onContinue: () => void
}

/**
 * Create-quotation gate (REQ-377). Blocks quotation creation while the drawing has no project number,
 * capturing it inline. On Continue it stores the number (shared with the drawer header editor) and
 * hands control back to the caller to open the create-quotation flow.
 */
export const ProjectNumberGateModal = ({ isOpen, onClose, onContinue }: Props) => {
	const { projectNumber, setProjectNumber } = useProjectNumber()
	const [draft, setDraft] = useState(projectNumber)
	const [isSubmitting, setIsSubmitting] = useState(false)

	// Seed the field from the stored value each time the modal opens.
	useEffect(() => {
		if (isOpen) setDraft(projectNumber)
	}, [isOpen, projectNumber])

	const canContinue = draft.trim().length > 0 && !isSubmitting

	const handleContinue = async () => {
		// Guard against double submit (Enter + click, or a second press while the PATCH is in flight):
		// each would fire a duplicate PATCH and open the create flow twice.
		if (!canContinue) return
		setIsSubmitting(true)
		try {
			// Persist the number before proceeding so the create-quotation request sees it server-side.
			const ok = await setProjectNumber(draft)
			if (ok) onContinue()
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<Modal isModalOpen={isOpen} setModalClose={onClose} width={456}>
			<div className={style.body} data-testid="project-number-gate">
				<h3 className={style.title}>Add project number first</h3>
				<p className={style.description}>To create a quotation, add a project number to this drawing first.</p>
				<AntInput
					autoFocus
					className={style.input}
					size="large"
					placeholder="Enter project number"
					value={draft}
					disabled={isSubmitting}
					onChange={(e: TOnChangeInput) => setDraft(e.target.value)}
					onPressEnter={handleContinue}
				/>
				<div className={style.actions}>
					<ButtonSecondary type="button" onClick={onClose}>
						Cancel
					</ButtonSecondary>
					<ButtonPrimary onClick={handleContinue} submittable={canContinue}>
						Continue
					</ButtonPrimary>
				</div>
			</div>
		</Modal>
	)
}
